import React, { useState } from "react"
import { FormattedMessage, IntlShape, useIntl } from "react-intl"
import { collapseSideBar } from "context/applicationContext"
import SubHeader from "components/Shared/SubHeader"
import * as paths from "constants/paths"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import {
	BRANDS,
	COUNTIES,
	LOCATION_GROUP_FILTER,
	PRODUCTS,
	PRODUCT_GROUP_FILTER,
	REGIONS,
	STORES,
	CHANNEL_GROUP_FILTER,
	CHANNEL,
	AGE,
	GENDER,
	SEGMENTATION_GROUP_FILTER,
	PSYCHOGRAPHICS,
	FINELINE,
} from "components/Shared/GlobalFilter/constants"
import Filters from "components/Shared/Filters"
import { filtersToShow } from "./Shared/utils"
import Selectors, {
	DEFAULT_PROPS,
} from "components/Shared/Filters/modules/Vendor"
import Location from "components/Shared/Filters/modules/Location"
import Product from "components/Shared/Filters/modules/Product"
import Segmentation from "components/Shared/Filters/modules/Segmentation"
import Channel from "components/Shared/Filters/modules/Channel"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import "./index.scss"
import NavTabs from "../LoyaltyPerformance/Loyalty/NavTabs"
import SegmentationSummary from "./Tabs/SegmentationSummary"
import HabitsSegmentation from "./Tabs/HabitsSummary"
import DemographicSegmentation from "./Tabs/DemographicSegmentation"
import { FiltersType } from "components/Shared/GlobalFilter/context/interfaces"
import { Skeleton, Alert } from "@walmart-web/livingdesign-components"
import PhronesisCalendar from "components/Shared/PhronesisCalendar/PhronesisCalendar"
import {
	DateInterval,
	getDatesFromRanges,
} from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"
import {
	CustomerProfilingInformation,
	getCustomerProfilingInformation,
} from "./service"

const panelContent: Record<string, JSX.Element> = {
	[LOCATION_GROUP_FILTER]: <Location />,
	[PRODUCT_GROUP_FILTER]: <Product />,
	[CHANNEL_GROUP_FILTER]: <Channel />,
	[SEGMENTATION_GROUP_FILTER]: <Segmentation />,
}

const filtersProperties = {
	...DEFAULT_PROPS,
	[FINELINE]: {
		allOption: true,
		isMulti: true,
	},
}

const generateTabs = (
	period: DateInterval,
	filters: FiltersType,
	intl: IntlShape
) => {
	return [
		{
			text: intl.formatMessage({
				id:
					"phronesis.customerbehavior.application.customerProfiling.tabs.title.segmentationSummary",
			}),
			pathname:
				paths.ROOT_CONTEXT + paths.CUSTOMER_PROFILING_SEGMENTATION_SUMMARY,
			component: <SegmentationSummary period={period} filters={filters} />,
		},
		{
			text: intl.formatMessage({
				id:
					"phronesis.customerbehavior.application.customerProfiling.tabs.title.demographicSegmentation",
			}),
			pathname:
				paths.ROOT_CONTEXT + paths.CUSTOMER_PROFILING_DEMOGRAPHIC_SEGMENTATION,
			component: <DemographicSegmentation period={period} filters={filters} />,
		},
		{
			text: intl.formatMessage({
				id:
					"phronesis.customerbehavior.application.customerProfiling.tabs.title.habitsSegmentation",
			}),
			pathname:
				paths.ROOT_CONTEXT + paths.CUSTOMER_PROFILING_HABITS_SEGMENTATION,
			component: (
				<HabitsSegmentation
					period={period}
					filters={filters}
				></HabitsSegmentation>
			),
		},
	]
}

const CustomerProfiling: React.FC = () => {
	collapseSideBar()
	const intl = useIntl()
	const { filters, interval } = useFiltersContext()
	const [dateInterval, setDateInterval] = useState<DateInterval>(
		getDatesFromRanges(interval)
	)

	const {
		data: dataCustomerInformation,
		isFetching: isFetchingInformationData,
	} = useFetchData<CustomerProfilingInformation>(
		"retrieveCustomerProfilingInformation",
		getCustomerProfilingInformation,
		{
			filters,
			period: dateInterval,
		},
		{
			enabled: true,
			retry: false,
		}
	)
	const menuButtons = [
		{
			title: intl.formatMessage({ id: "phronesis.filters.brandandproduct" }),
			toOpen: PRODUCT_GROUP_FILTER,
			dependencies: [BRANDS, PRODUCTS],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.channel" }),
			toOpen: CHANNEL_GROUP_FILTER,
			dependencies: [CHANNEL],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.groups.location" }),
			toOpen: LOCATION_GROUP_FILTER,
			dependencies: [REGIONS, COUNTIES, STORES],
		},
		{
			title: intl.formatMessage({ id: "phronesis.filters.segmentation" }),
			toOpen: SEGMENTATION_GROUP_FILTER,
			dependencies: [AGE, GENDER, PSYCHOGRAPHICS],
		},
	]

	const renderMessage = () => {
		return isFetchingInformationData ? (
			<Skeleton height="1.5rem" />
		) : (
			<Alert UNSAFE_className="message-description" variant="info">
				<FormattedMessage
					id="phronesis.customerbehavior.application.customerProfiling.subheader"
					values={{
						percentage: intl.formatNumber(
							dataCustomerInformation.percentageByCustomers,
							{
								style: "percent",
								minimumFractionDigits: 1,
							}
						),
						total: intl.formatNumber(dataCustomerInformation.totalCustomers, {
							style: "decimal",
							maximumFractionDigits: 0,
						}),
						b: (str: any) => <b>{str}</b>,
					}}
				/>
			</Alert>
		)
	}
	return (
		<div className="customer-profiling">
			<SubHeader
				title={"phronesis.menu.option.customerbehavior.title"}
				subTitle={"phronesis.customerbehavior.application.customerProfiling"}
				titleContent={
					<div className="phronesis-calendar">
						<PhronesisCalendar
							setDateInterval={(interval) => {
								setDateInterval(interval)
							}}
						/>
					</div>
				}
				parentPath={paths.CUSTOMER_BEHAVIOR}
				filters={filtersToShow}
				content={
					<Filters dataMenu={{ panelContent, buttons: menuButtons }}>
						<Selectors selectProps={filtersProperties} />
					</Filters>
				}
			/>
			{renderMessage()}
			<NavTabs tabs={generateTabs(dateInterval, filters, intl)} />
		</div>
	)
}

export default CustomerProfiling
